﻿import React, { useState, useEffect, useRef } from "react";
import CampaignItem from "./CampaignItem";
import FacilityInfoItem from "./FacilityInfoItem";
import { search } from "../Endpoints";
import { myAimoParkConfirm } from "../../../_Shared/Scripts/MyAimoParkConfirm";
import controller from "../../../../../Resources/Scripts/_Shared/controller";
import moment from "moment";
import { parseDate } from "../../../../../Resources/Scripts/_Shared/parseDate";

const ExpandableListItem = ({
    STPMode,
    campaignsPending,
    facilitySearchContext,
    uid,
    date,
    expanded,
    products,
    filteredNotes,
    notesForZones,
    facilityInfo,
    hasPrivatePmcsOnly,
}) => {
    const [selectedTab, setSelectedTab] = useState("info");
    const [allPricesProducts, setAllPricesProducts] = useState(null);
    const [pricesProducts, setPricesProducts] = useState(null);
    const [hasRestrictedPrivatePmcs, setHasRestrictedPrivatePmcs] = useState(false);
    const [previousDate, setPreviousDate] = useState(null);
    const [pending, setPending] = useState(null);
    const priceListRef = useRef(null);
    const expandedListNav = useRef(null);
    // Private PMC sign-in messaging should be visible JUST in Sweden - no booking flow in Finland/Norway
    const isSweden = facilitySearchContext.country === "se";

    products.Campaigns = products.Campaigns
        ? products.Campaigns.filter(
              (campaign) =>
                  !campaign.DateTo ||
                  moment(parseDate(date)).isBefore(campaign.DateTo),
          )
        : [];
    const numberOfCampaigns = products.Campaigns.length;

    const lang = {
        ...facilitySearchContext.localization.headings,
        ...facilitySearchContext.localization.inputs,
        ...facilitySearchContext.localization.products,
    };

    const getNotesForZone = (groupedProduct) => {
        if (!groupedProduct[0].PlaceUid) return null;
        const zoneInformation = notesForZones.find(
            (note) =>
                note.filteredNotes &&
                note.ZoneId &&
                note.ZoneId === groupedProduct[0].PlaceUid,
        );
        if (!zoneInformation) return null;
        return zoneInformation.filteredNotes;
    };

    const getSTPorLTPprices = (priceList) => {
        const priceListDiv = document.createElement("div");
        priceListDiv.innerHTML = priceList;

        const productsEl = priceListDiv.querySelector(".facilitypage__products");
        setHasRestrictedPrivatePmcs(productsEl?.classList.contains("has-restricted-private-pmcs") ?? false);

        if (productsEl && productsEl.children.length === 0) {
            productsEl.remove();
        }

        setPricesProducts(priceListDiv.innerHTML.trim() || null);
    };

    useEffect(() => {
        if (!expanded) return;
        let isSubscribed = true;
        const getPriceList = async () => {
            if (pricesProducts && date === previousDate) return;
            setPending(true);
            const priceList = await search.getPricesProducts({
                fId: uid,
                epslanguage: facilitySearchContext.episerver.selectedlanguage,
                availabilityFrom: facilitySearchContext.searchDate,
            });
            if (priceList.status === 200 && isSubscribed) {
                setAllPricesProducts(priceList.body);
                getSTPorLTPprices(priceList.body);
            }
            setPreviousDate(date);
            setPending(false);
        };
        getPriceList();
        return () => (isSubscribed = false);
    }, [expanded, date, STPMode]);

    useEffect(() => {
        if (!allPricesProducts) return;
        getSTPorLTPprices(allPricesProducts);
    }, [STPMode]);

    useEffect(() => {
        if (!pending && expanded && selectedTab === "pricelist") {
            controller.add("myaimopark-confirm", myAimoParkConfirm);
            controller.parse(priceListRef.current);
        }
    }, [selectedTab, pending]);

    const handleNavScroll = () => {
        const nav = expandedListNav.current;
        const shadowLeft = nav.previousElementSibling;
        const shadowRight = nav.nextElementSibling;
        if (nav.scrollLeft > 5) shadowLeft.classList.add("visible");
        else shadowLeft.classList.remove("visible");
        if (nav.scrollLeft < nav.scrollWidth - nav.clientWidth - 5)
            shadowRight.classList.add("visible");
        else shadowRight.classList.remove("visible");
    };

    return (
        <div
            className="facilitysearchpage__listitem__expanded"
            aria-hidden={!expanded}
        >
            <span className="shadow shadow--left"></span>
            <nav
                ref={expandedListNav}
                onScroll={handleNavScroll}
                role="tablist"
                className="facilitysearchpage__listitem__tabs"
            >
                <button
                    role="tab"
                    aria-selected={selectedTab === "info"}
                    onClick={() => setSelectedTab("info")}
                >
                    {lang.infotab}
                </button>
                <button
                    role="tab"
                    aria-selected={selectedTab === "pricelist"}
                    onClick={() => setSelectedTab("pricelist")}
                >
                    {lang.productsprices}
                </button>
                <button
                    role="tab"
                    aria-selected={selectedTab === "offers"}
                    onClick={() => setSelectedTab("offers")}
                >
                    {lang.currentoffers.split(" ").slice(0, 2).join(" ")}
                    {numberOfCampaigns && !STPMode
                        ? ` (${numberOfCampaigns})`
                        : " (0)"}
                </button>
            </nav>
            <span className="shadow shadow--right visible"></span>
            <div
                className="facilitysearchpage__listitem__tabs__content"
                aria-busy={pending || campaignsPending}
            >
                {selectedTab === "info" && (
                    <FacilityInfoItem
                        STP={STPMode}
                        facilityInfo={facilityInfo}
                        lang={lang}
                    />
                )}
                {selectedTab === "pricelist" && (
                    <>
                        <div
                            dangerouslySetInnerHTML={{ __html: pricesProducts }}
                            ref={priceListRef}
                        />
                        {isSweden && pending === false && pricesProducts && facilityInfo.HasPrivatePmcs && !facilitySearchContext.episerver.isLoggedIn && (
                            <div>{lang.privatepmcmore}</div>
                        )}
                        {isSweden && pending === false && pricesProducts && hasRestrictedPrivatePmcs && facilitySearchContext.episerver.isLoggedIn && (
                            <div>{lang.privatepmcmoreloggedin}</div>
                        )}
                        {pending === false && !pricesProducts && (
                            <div>
                                {isSweden && hasPrivatePmcsOnly && !facilitySearchContext.episerver.isLoggedIn
                                    ? lang.privatepmc
                                    : isSweden && hasPrivatePmcsOnly && facilitySearchContext.episerver.isLoggedIn
                                        ? lang.privatepmcnoaccess
                                        : lang.noproducts}
                            </div>
                        )}
                        {pending && (
                            <div className="facilitysearchpage__listitem__tabs__content__pending">
                                <div className="aimo-spinner">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}
                    </>
                )}
                {selectedTab === "offers" && (
                    <>
                        {numberOfCampaigns && !STPMode ? (
                            <ul className="facilitysearchpage__listitem__campaignlist">
                                {products.Campaigns.map((campaign) => (
                                    <CampaignItem
                                        key={campaign.Image}
                                        campaign={campaign}
                                    />
                                ))}
                            </ul>
                        ) : (
                            !campaignsPending && <div>{lang.noitems}</div>
                        )}
                        {campaignsPending && (
                            <div className="facilitysearchpage__listitem__tabs__content__pending">
                                <div className="aimo-spinner">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ExpandableListItem;
